import { useState, useContext } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { LocaleContext } from "../../routing/LangRouter";
import axios from "axios";
import { useTranslation } from "react-i18next";

//import constants
import BASE_URL from "../../constants/baseUrls";

//import utils
import { fixNumbers } from "../../utils/utility";

//import general components
import BaseTextField from "../../components/common/base-text-field/base-text-field";
import { BaseButton } from "../../components/common/base-button/base-button";

//import view components
import VerificationCode from "./verification-code";

//import styles
import "./signIn.scss";

function SignIn({ handleClose }) {
    //general variables
    const navigate = useNavigate();
    const location = useLocation();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    //state to toggle between sign-in and verification code views
    const [isSignInView, setIsSignInView] = useState(true);

    const [isLoading, setIsLoading] = useState({ getCode: false, confirmCode: false });

    //validation options
    const validationOptions = {
        username: {
            required: `${t("email")} ${t("or")} ${t("phoneNumber")} ${t("isRequired")}`,
            validate: {
                validateNumber: value => {
                    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                    const phonePattern = /^\d{11}$/;

                    if (emailPattern.test(fixNumbers(value)) || phonePattern.test(fixNumbers(value))) {
                        return true;
                    }

                    return `${t("email")} ${t("or")} ${t("phoneNumber")} ${t("isWrong")}`;
                },
            },
        },
    };

    //initialize form handling
    const {
        register,
        handleSubmit,
        resetField,
        formState: { errors },
    } = useForm();

    //handle registration
    const handleRegistration = data => {
        getOtpCode(data);
    };

    //request an OTP code
    const [formattedUsername, setFormattedUsername] = useState("");
    const [isNewUser, setIsNewUser] = useState(false);
    const getOtpCode = async formData => {
        setIsLoading(prevState => ({ ...prevState, getCode: true }));
        const username = fixNumbers(formData.username);
        setFormattedUsername(username);

        try {
            const response = await axios.get(`${BASE_URL}/api/v2/login/`, { params: { username: username } });
            setIsNewUser(response.data.data.is_new_user);
            setIsSignInView(false);
            toast.success(t("successMessage"), { toastId: "custom-id-yes" });
        } catch (error) {
            const errormessage = locale === "fa" ? error.response.data.errors.otp?.[0] : t("errorMessage");
            toast.error(errormessage, { toastId: "custom-id-yes" });
        } finally {
            setIsLoading(prevState => ({ ...prevState, getCode: false }));
        }
    };

    //handles the login request with a verification code
    const confirmVerificationCode = async (event, otpCode, friendsIntroductionValues) => {
        event.preventDefault();
        setIsLoading(prevState => ({ ...prevState, confirmCode: true }));
        const formData = createFormData(formattedUsername, fixNumbers(otpCode), friendsIntroductionValues);

        try {
            const response = await axios.post(`${BASE_URL}/api/v2/login/`, formData);
            storeUserTokens(response.data.data.auth, response.data?.data.user);
            toast.success(t("loginMessage1"), { toastId: "custom-id-yes" });
            handleLoginNavigation();
            setIsSignInView(true);
            resetField("username");
        } catch (error) {
            toast.error(t("loginMessage2"), { toastId: "custom-id-yes" });
        } finally {
            setIsLoading(prevState => ({ ...prevState, confirmCode: false }));
        }
    };

    //create a FormData object with phone number and OTP for login request
    const createFormData = (username, otp, friendsIntroductionValues) => {
        const instagramId = friendsIntroductionValues?.instagramId;
        const cleanedId = instagramId?.startsWith("@") ? instagramId.slice(1) : instagramId;
        const data = new FormData();
        data.append("username", username);
        data.append("otp", otp);
        data.append("representative_code", friendsIntroductionValues?.representativeCode);
        data.append("instagram_id", cleanedId);
        return data;
    };

    //store user tokens and information in local storage
    const storeUserTokens = ({ access_token, refresh_token, expires_in }, userInformation) => {
        localStorage.setItem("accessToken", access_token);
        localStorage.setItem("refreshToken", refresh_token);
        localStorage.setItem("expirationDate", expires_in);
        localStorage.setItem("userInformation", JSON.stringify(userInformation));
    };

    //handles the navigation after a successful login
    const handleLoginNavigation = () => {
        if (location.pathname === `/${locale}/login`) {
            const targetLocation = location.state?.from?.pathname || `/${locale}`;
            navigate(targetLocation, { replace: true });
        } else {
            handleClose();
        }
    };

    //resend code for log in with phone number
    const resendOtpCode = () => {
        getOtpCode({ username: formattedUsername });
    };

    return (
        <>
            {isSignInView ? (
                <Grid container item xs={12} rowGap={2}>
                    <span className="signIn__title">{t("signinTitle")}:</span>
                    <Grid
                        component="form"
                        container
                        item
                        xs={12}
                        rowGap={2}
                        justifyContent={{ xs: "center", md: "flex-end" }}
                        onSubmit={handleSubmit(handleRegistration)}
                    >
                        <BaseTextField
                            dir="ltr"
                            label={`${t("email")} ${t("or")} ${t("phoneNumber")}`}
                            size="small"
                            type="text"
                            name="username"
                            register={{ ...register("username", validationOptions.username) }}
                            error={errors?.username?.message}
                            radius="10px"
                        />

                        <Grid container item xs={8} md={4}>
                            <BaseButton
                                text={t("verificationCode")}
                                color="primary"
                                size="medium"
                                radius="radius-10"
                                type="submit"
                                loading={isLoading.getCode}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            ) : (
                <VerificationCode
                    confirmVerificationCode={confirmVerificationCode}
                    resendOtpCode={resendOtpCode}
                    loading={isLoading}
                    username={formattedUsername}
                    setIsSignInView={setIsSignInView}
                    isNewUser={isNewUser}
                />
            )}
        </>
    );
}

export default SignIn;
